import { AlertTriangle, RefreshCw } from 'lucide-react';

import { Component, useEffect, type ReactNode } from 'react';
import { useRouteError } from 'react-router';

import { isChunkReloadInFlight, hasExhaustedChunkReload, resetChunkReloadGuard } from '../../utils/chunkReload';

function handleChunkReload() {
  resetChunkReloadGuard(); // deliberate user retry, not an automatic loop
  window.location.reload();
}

function ErrorFallback({ onRetry, error }: { onRetry: () => void; error?: unknown }) {
  const chunkReloading = isChunkReloadInFlight();
  const chunkExhausted = !chunkReloading && hasExhaustedChunkReload();

  const title = chunkReloading
    ? 'Updating'
    : chunkExhausted
      ? "We couldn't load part of the app"
      : 'Something went wrong';
  const message = chunkReloading
    ? 'The app is reloading to pick up the latest version.'
    : chunkExhausted
      ? 'This usually clears up after a refresh.'
      : 'An unexpected error occurred. Please try again or refresh the page.';

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f5f7fb] dark:bg-[#09090f] p-6">
      <div className="max-w-xl w-full bg-white dark:bg-[#131320] rounded-2xl shadow-xl border border-[#e2e8f0] dark:border-white/[0.08] p-8 text-center space-y-5">
        <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-500/15 flex items-center justify-center">
          <AlertTriangle className="size-8 text-amber-600 dark:text-amber-400" />
        </div>
        <h2 className="text-xl font-bold text-[#0f172a] dark:text-white">
          {title}
        </h2>
        <p className="text-sm text-[#64748b] dark:text-white/50">
          {message}
        </p>

        {!chunkReloading && (
          <div className="flex justify-center gap-3">
            <button
              onClick={chunkExhausted ? handleChunkReload : onRetry}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-[13px] font-bold bg-indigo-600 dark:bg-indigo-600 text-white hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-all shadow-sm"
            >
              <RefreshCw className="size-4" />
              {chunkExhausted ? 'Reload' : 'Try Again'}
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-[13px] font-bold text-[#64748b] dark:text-white/50 bg-[#f1f5f9] dark:bg-white/[0.05] hover:bg-[#e2e8f0] dark:hover:bg-white/[0.08] transition-all"
            >
              Go Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * For non-router children of <App> (ThemeProvider/ToastListener/etc). A data
 * router's own errors never reach this - see RouteErrorBoundary below.
 */
interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // A reload from vite:preloadError is already in flight for this exact
    // failure - no need to also report it.
    if (!isChunkReloadInFlight()) {
      console.error('ErrorBoundary caught:', error, info.componentStack);
    }
  }

  componentDidMount() {
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  componentWillUnmount() {
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    event.preventDefault();
    console.error('Unhandled rejection:', event.reason);
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return <ErrorFallback onRetry={this.handleReset} error={this.state.error} />;
    }

    return this.props.children;
  }
}

export function RouteErrorBoundary() {
  const error = useRouteError();

  useEffect(() => {
    if (!isChunkReloadInFlight()) {
      console.error('Route error:', error);
    }
  }, [error]);

  return <ErrorFallback onRetry={() => window.location.reload()} error={error} />;
}
